import React, { useState, useEffect, useCallback } from 'react';
import { signIn, signOut, getCurrentUser } from 'aws-amplify/auth';
import { client } from './amplify';
import {
  getUser as GetUser,
  createUser as CreateUser,
  //getBoxes as GetBoxes,
  createUserBox,
  createUserStack,
} from './graphql/graphql';
import { mark } from './perf';

export const UserContext = React.createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  const checkIfUserExists = useCallback(
    async (id) =>
      await client
        .graphql({ query: GetUser, variables: { id } })
        .then((user) => {
          const { getUser } = user.data;
          if (!getUser) {
            const newUser = createUser(id);
            return newUser;
          }
          return getUser;
        })
        .catch((err) => {
          throw err;
        }),
    []
  );

  const createUser = async (username) =>
    await client
      .graphql({
        query: CreateUser,
        variables: { username: username, id: username },
      })
      .then(async (user) => {
        const bb = await client.graphql({
          query: createUserBox,
          variables: {
            ownerId: username,
            private: true,
            name: 'blackbox',
            type: 'BLACK',
          },
        });
        const wb = await client.graphql({
          query: createUserBox,
          variables: {
            ownerId: username,
            private: false,
            name: 'whitebox',
            type: 'WHITE',
          },
        });
        await Promise.all([
          client.graphql({
            query: createUserStack,
            variables: {
              boxId: bb.data.createBox.id,
              private: true,
              name: 'unsorted',
            },
          }),
          client.graphql({
            query: createUserStack,
            variables: {
              boxId: wb.data.createBox.id,
              private: false,
              name: 'unsorted',
            },
          }),
        ]);
      })
      .then(async (username) => {
        const user = await client.graphql({
          query: GetUser,
          variables: { username },
        });
        return user;
      })
      .catch((err) => {
        throw err;
      });

  // Attaches the profile collections to a signed-in user. v6's getCurrentUser
  // returns a plain { username, userId } object rather than v3's CognitoUser
  // class instance, so these can be spread into a new object instead of being
  // bolted onto a live instance by mutation -- which is what the old
  // `profileVersion` counter existed to work around.
  const withProfile = useCallback(
    async (account) => {
      const profile = await checkIfUserExists(account.username);
      return {
        ...account,
        origamis: profile.origamis || {},
        boxes: profile.boxes || {},
        gifts: profile.gifts || {},
      };
    },
    [checkIfUserExists]
  );

  useEffect(() => {
    // attempt to fetch the info of the user that was already logged in
    getCurrentUser()
      .then((account) => {
        // Give the collections their empty shape immediately so anything that
        // renders before the profile query lands sees the right structure.
        setUser({ ...account, origamis: {}, boxes: {}, gifts: {} });

        // Release the render here. Waiting for checkIfUserExists put a second
        // GraphQL round trip in front of the first paint for no benefit --
        // the profile it fetches is only read further down the sidebar tree.
        setIsAuthenticating(false);
        mark('auth:resolved');

        return withProfile(account)
          .then((account) => {
            mark('profile:resolved');
            setUser(account);
          })
          .catch((err) => {
            // A failed profile fetch leaves the user signed in with empty
            // collections rather than bouncing them to the homepage.
            console.error('Failed to load user profile', err);
          });
      })
      .catch((error) => {
        // v6 rejects with UserUnAuthenticatedException when nobody is signed
        // in; this is the ordinary signed-out path, not a failure.
        console.log(error);
        setUser(null);
        setIsAuthenticating(false);
      });
  }, [withProfile]);

  // Returns Amplify's { isSignedIn, nextStep } so the sign-in form can route
  // the challenge steps (unconfirmed account, forced password reset). v3's
  // Auth.signIn resolved to the user itself and signalled challenges through a
  // `challengeName` property on it; v6 never returns a user, so the account is
  // fetched separately once the flow reports it is done.
  const login = async (usernameOrEmail, password) => {
    // Marks the machine time of an interactive sign-in, isolated from the human
    // time spent on the login form. login:submit -> login:authed is the Cognito
    // round trip; login:authed -> login:profile is the getUser query, which is
    // awaited here before returning, so on a fresh login it sits on the critical
    // path in front of the box mount (the deferral fix only helps the
    // already-signed-in reload path in the effect above).
    mark('login:submit');
    const result = await signIn({
      username: usernameOrEmail,
      password,
    });
    mark('login:authed');
    if (!result.isSignedIn) {
      return result;
    }

    const account = await getCurrentUser();
    setUser(await withProfile(account));
    mark('login:profile');
    return result;
  };

  // same thing here
  const logout = async () => {
    const data = await signOut();
    setUser(null);
    return data;
  };

  // The WithSignIn / WithUser higher-order components that used to live here have
  // been removed: both were called during render at their use sites, which gave
  // the wrapped component a fresh identity every render and remounted it.
  // Consumers now read `user` and `login` from context directly.

  // Keep the context value referentially stable so consumers only re-render when
  // the user or the authenticating flag actually changes. This memo existed but
  // was commented out, so every provider render invalidated every consumer.
  const values = React.useMemo(
    () => ({
      user,
      login,
      logout,
      isAuthenticating,
    }),
    // login/logout close over setUser and withProfile, both of which are
    // stable, so they do not need to participate in the dependency list.
    // profileVersion is gone with them: the profile now arrives as a new user
    // object, so `user` changing identity is what tells consumers to re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, isAuthenticating]
  );

  // Finally, return the interface that we want to expose to our other components
  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
};

// We also create a simple custom hook to read these values from. We want our React components
// to know as little as possible on how everything is handled, so we are not only abtracting them from
// the fact that we are using React's context, but we also skip some imports.
export const useUser = () => {
  const context = React.useContext(UserContext);

  if (context === undefined) {
    throw new Error(
      '`useUser` hook must be used within a `UserProvider` component'
    );
  }
  return context;
};
